import {
  Add,
  Approval,
  PersonAdd,
  RocketLaunch,
  Save,
  Send,
  SubdirectoryArrowLeft,
  Undo,
} from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import useApi, { httpClient } from "../hooks/useApi";
import { messageActions } from "../store/store";
import Constants from "../utils/Constants";
import { buildUserOptionLabel } from "../utils/utils";
import OwnedStockConfirmation from "./Comfirmation/OwnedStockConfirmation";
import ReceivablePayableConfirmation from "./Comfirmation/ReceivablePayableConfirmation";
import ConfirmerForm from "./Form/ConfirmerForm";
import FormDialog from "./FormDialog";

const ResponeseComponent = forwardRef(
  (
    {
      targetData,
      presenceOrAbsenceList,
      postSaveHandler = () => {},
      isApproved,
      readOnly = false,
    },
    ref
  ) => {
    const confirmationDataRef = useRef();
    const dispatch = useDispatch();
    const { connect } = useApi();

    useEffect(() => {
      // 「次の操作を選択する」ボタンの表示制御
      // 確認状ステータス：送付済み or
      // 確認状ステータス：差戻し or
      // 確認状ステータス：承認待ち and 確認先担当者の権限が「承認者」の場合のみ表示
      setDisplaySelectNextActionButton(
        targetData?.confirmationLetterStatus ===
          Constants.CONFIRMATION_LETTER_STATUS.SHIPPED ||
          targetData?.confirmationLetterStatus ===
            Constants.CONFIRMATION_LETTER_STATUS
              .APPROVAL_REQUEST_REMANDED_BY_CONFIRMER ||
          (targetData?.confirmationLetterStatus ===
            Constants.CONFIRMATION_LETTER_STATUS
              .WAITING_FOR_APPROVAL_OF_CONFIRMER &&
            targetData?.confirmerAuthority ===
              Constants.CONFIRMER_AUTHORITY_TYPE.APPROVER)
      );
    }, [targetData?.confirmationLetterStatus, targetData?.confirmerAuthority]);

    // 保存処理
    const handleSave = async () => {
      const result = await connect(() => {
        return httpClient.post(
          `/api/confirmer/UpsertConfirmation`,
          confirmationDataRef?.current?.getFormData()
        );
      }, "保存しました");

      if (result?.status === 200) {
        confirmationDataRef?.current?.resetIsDirty();
      }
    };

    // 承認依頼の取下げ関連
    const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] =
      useState(false);
    const handleCancelApprovalRequest = async () => {
      await connect(() => {
        return httpClient.post(
          `/api/confirmer/cancelApprovalRequest?confirmationLetterId=${targetData.id}`
        );
      }, "承認依頼を取り下げました");
      setIsCancelConfirmDialogOpen(false);
      postSaveHandler();
    };

    // 回答送信処理
    const handleConfirmSendResponse = async () => {
      await connect(() => {
        return httpClient.post(
          `/api/confirmer/SendResponse?confirmationLetterId=${targetData.id}`
        );
      }, "回答を送信しました");
      postSaveHandler();
    };

    // 差戻し関連
    const [isOnRemandDialogOpen, setIsOnRemandDialogOpen] = useState(false);
    const [remandComment, setRemandComment] = useState("");
    const handleRemand = async () => {
      await connect(() => {
        return httpClient.post("/api/confirmer/remandApprovalRequest", {
          confirmationLetterId: targetData.id,
          remandComment: remandComment,
        });
      }, "差戻しました");
      setIsOnRemandDialogOpen(false);
      postSaveHandler();
    };

    // ↓↓↓↓↓↓↓↓↓↓↓↓↓↓「次の操作選択する」ボタン関連
    const [openSelectNextActionDialog, setOpenSelectNextActionDialog] =
      useState(false);
    const [selectedAction, setSelectedAction] = useState(null);
    const [selectedUser, setSelectedUser] = useState("");
    const [confirmerList, setConfirmerList] = useState([]);
    const [confirmerListUpdater, setConfirmerListUpdater] = useState(0);
    const [selectNextActionDialogTitle, setSelectNextActionDialogTitle] =
      useState("");
    const [nextActionButtonLabel, setNextActionButtonLabel] = useState("");
    const [displaySelectNextActionButton, setDisplaySelectNextActionButton] =
      useState(false);

    // ダイアログを開く関数
    const handleOpenSelectNextActionDialog = () => {
      if (!readOnly && confirmationDataRef?.current?.getIsDirty()) {
        // 確認状ステータスが「送付済み」かつ変更のある場合は自動保存
        handleSave();
      }
      setOpenSelectNextActionDialog(true);
      setSelectNextActionDialogTitle("次のアクションを選択してください");
      setNextActionButtonLabel("");
      setSelectedAction(null);
      setSelectedUser("");
    };

    // 操作アクション一覧
    const actions = [
      {
        label: "入力依頼",
        description: "社内の方に入力作業を依頼します。",
        color: "secondary",
        icon: <PersonAdd />,
        onClick: () => {
          setSelectedAction(Constants.CONFIRMER_ACTIONS.INPUT_REQUEST);
          setSelectNextActionDialogTitle("入力依頼を行います");
          setNextActionButtonLabel("入力依頼");
          setConfirmerListUpdater(confirmerListUpdater + 1);
        },
        display: !readOnly,
      },
      {
        label: "承認依頼",
        // 確認状ステータスで説明文を切り替える
        description:
          targetData?.confirmationLetterStatus ===
          Constants.CONFIRMATION_LETTER_STATUS.WAITING_FOR_APPROVAL_OF_CONFIRMER
            ? "承認して、更に別の承認者に承認を依頼します。"
            : "社内の承認者に承認を依頼します。",
        color: "warning",
        icon: <Approval />,
        onClick: async () => {
          if (await validate()) {
            setSelectedAction(Constants.CONFIRMER_ACTIONS.APPROVAL_REQUEST);
            setSelectNextActionDialogTitle("承認依頼を行います");
            setNextActionButtonLabel("承認依頼");
            setConfirmerListUpdater(confirmerListUpdater + 1);
          }
        },
        display: true,
      },
      {
        label: "回答送信",
        // 確認状ステータスで説明文を切り替える
        description:
          targetData?.confirmationLetterStatus ===
          Constants.CONFIRMATION_LETTER_STATUS.WAITING_FOR_APPROVAL_OF_CONFIRMER
            ? "承認して、監査法人に回答を送信します。"
            : "監査法人に回答を送信します。",
        color: "success",
        icon: <Send />,
        onClick: () => {
          if (validate()) {
            setSelectedAction(Constants.CONFIRMER_ACTIONS.SUBMIT_RESPONSE);
            setSelectNextActionDialogTitle("回答送信を行います");
            setNextActionButtonLabel("回答送信");
          }
        },
        display: true,
      },
    ];

    // アクションボタンがクリックされたときの処理
    const handleActionClick = async () => {
      switch (selectedAction) {
        case Constants.CONFIRMER_ACTIONS.INPUT_REQUEST:
          // 入力依頼処理
          await connect(() => {
            return httpClient.post(`/api/confirmer/RequestInput`, {
              confirmationLetterId: targetData.id,
              requestedTo: selectedUser,
            });
          }, "入力依頼を行いました");
          setOpenSelectNextActionDialog(false);
          break;
        case Constants.CONFIRMER_ACTIONS.APPROVAL_REQUEST:
          // 承認依頼処理
          await connect(() => {
            return httpClient.post(`/api/confirmer/RequestApproval`, {
              confirmationLetterId: targetData.id,
              approverId: selectedUser,
            });
          }, "承認依頼を行いました");
          setOpenSelectNextActionDialog(false);
          postSaveHandler();
          break;
        case Constants.CONFIRMER_ACTIONS.SUBMIT_RESPONSE:
          // 回答送信確認ダイアログを表示
          handleConfirmSendResponse();
          setOpenSelectNextActionDialog(false);
          break;
        default:
          break;
      }
    };

    // バリデーション発火
    const validate = async () => {
      const valid = await confirmationDataRef?.current?.validate();
      if (!valid) {
        setOpenSelectNextActionDialog(false);
        dispatch(
          messageActions.setError(
            "入力内容に不備があります。確認してください。"
          )
        );
        return false;
      }
      return true;
    };

    // 確認先担当者追加ダイアログ関連
    const [addConfirmerDialogOpen, setAddConfirmerDialogOpen] = useState(false);
    // 確認先担当者一覧取得
    useEffect(() => {
      const fetchConfirmerList = async () => {
        const result = await connect(() => {
          return httpClient.get(
            `/api/confirmer/GetConfirmerList?confirmationLetterId=${targetData?.id}`
          );
        });
        setConfirmerList(result?.data?.data);
      };
      fetchConfirmerList();
    }, [confirmerListUpdater, connect, targetData?.id]);

    return (
      <>
        {targetData?.confirmationType ===
          Constants.CONFIRMATION_LETTER_TYPE.OWNED_STOCK && (
          <OwnedStockConfirmation
            targetData={targetData}
            presenceOrAbsenceList={presenceOrAbsenceList}
            postSaveHandler={postSaveHandler}
            readOnly={readOnly}
            ref={confirmationDataRef}
          />
        )}
        {targetData?.confirmationType ===
          Constants.CONFIRMATION_LETTER_TYPE.RECEIVABLE_PAYABLE && (
          <>
            <Typography>{`本確認内容は、${targetData?.companyName}が${targetData?.referenceDateFormatted}現在、${targetData?.confirmationDestination}に対して計上している債権債務です。`}</Typography>
            <Typography>
              内容をご確認のうえ、相違がある場合は、「相違内容」欄に具体的な内容をご記入ください。
            </Typography>
            <ReceivablePayableConfirmation
              targetData={targetData}
              postSaveHandler={postSaveHandler}
              readOnly={readOnly}
              ref={confirmationDataRef}
            />
          </>
        )}
        {!readOnly && (
          <Button onClick={handleSave} startIcon={<Save />}>
            一時保存
          </Button>
        )}

        {/* 承認依頼取り下げ */}
        {targetData?.confirmerAuthority ===
          Constants.CONFIRMER_AUTHORITY_TYPE.APPROVAL_REQUESTER &&
          targetData?.confirmationLetterStatus ===
            Constants.CONFIRMATION_LETTER_STATUS
              .WAITING_FOR_APPROVAL_OF_CONFIRMER && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Undo />}
              onClick={() => setIsCancelConfirmDialogOpen(true)}
            >
              承認依頼の取り下げ
            </Button>
          )}

        {/* 差戻し */}
        {targetData?.confirmerAuthority ===
          Constants.CONFIRMER_AUTHORITY_TYPE.APPROVER &&
          targetData?.confirmationLetterStatus ===
            Constants.CONFIRMATION_LETTER_STATUS
              .WAITING_FOR_APPROVAL_OF_CONFIRMER && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SubdirectoryArrowLeft />}
              onClick={() => setIsOnRemandDialogOpen(true)}
            >
              差し戻し
            </Button>
          )}

        {/* 「次のアクションを選択する」ボタン */}
        {displaySelectNextActionButton && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<RocketLaunch />}
            onClick={() => handleOpenSelectNextActionDialog()}
          >
            次のアクションを選択する
          </Button>
        )}

        {/* 「次の操作を選択する」ダイアログ */}
        <Dialog
          open={openSelectNextActionDialog}
          onClose={() => setOpenSelectNextActionDialog(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ textAlign: "center" }}>
            {selectNextActionDialogTitle}
          </DialogTitle>
          <DialogContent sx={{ padding: "1rem" }}>
            {!selectedAction ? (
              // 操作アクション一覧
              <Stack spacing={2} sx={{ mt: 1 }}>
                {actions.map(
                  (action, index) =>
                    action.display && (
                      <Paper key={index} elevation={2} sx={{ p: 2 }}>
                        <Button
                          fullWidth
                          variant={
                            action.color === "success"
                              ? "contained"
                              : "outlined"
                          }
                          color={action.color}
                          startIcon={action.icon}
                          onClick={() => action.onClick()}
                          sx={{ mb: 1 }}
                        >
                          {action.label}
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          {action.description}
                        </Typography>
                      </Paper>
                    )
                )}
              </Stack>
            ) : selectedAction ===
              Constants.CONFIRMER_ACTIONS.SUBMIT_RESPONSE ? (
              // 回答送信画面
              <>
                <DialogContentText>
                  この操作は取り消すことが出来ません。
                  <br />
                  回答送信してもよろしいですか？
                </DialogContentText>
              </>
            ) : (
              // 入力依頼 or 承認依頼画面
              <>
                <TextField
                  label="担当者を選択"
                  select
                  required
                  fullWidth
                  onChange={(e) => setSelectedUser(e.target.value)}
                  value={selectedUser}
                >
                  {confirmerList?.map((confirmer) => (
                    <MenuItem
                      key={confirmer.id}
                      value={confirmer.id}
                      disabled={
                        confirmer.isApproved ||
                        confirmer.id === targetData?.confirmerAuthorityId
                      }
                    >
                      {buildUserOptionLabel(confirmer)}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="text"
                  sx={{ margin: 0, padding: 0 }}
                  startIcon={<Add />}
                  onClick={() => setAddConfirmerDialogOpen(true)}
                >
                  担当者を追加する
                </Button>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {!selectedAction ? (
              <></>
            ) : (
              <>
                <Button
                  onClick={handleOpenSelectNextActionDialog}
                  color="warning"
                >
                  戻る
                </Button>
                <Button
                  onClick={handleActionClick}
                  disabled={
                    !selectedUser &&
                    (selectedAction ===
                      Constants.CONFIRMER_ACTIONS.INPUT_REQUEST ||
                      selectedAction ===
                        Constants.CONFIRMER_ACTIONS.APPROVAL_REQUEST)
                  }
                  color="primary"
                  autoFocus
                >
                  {nextActionButtonLabel}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
        {/* 確認先担当者追加ダイアログ */}
        <FormDialog
          open={addConfirmerDialogOpen}
          onClose={() => setAddConfirmerDialogOpen(false)}
          title="担当者を追加する"
          maxWidth="sm"
        >
          <ConfirmerForm
            confirmationLetterId={targetData?.id}
            closeHandler={() => {
              setConfirmerListUpdater((prev) => prev + 1);
              setAddConfirmerDialogOpen(false);
            }}
            targetData={{}}
          />
        </FormDialog>
        {/* 承認依頼取り下げダイアログ */}
        <Dialog
          onClose={() => setIsCancelConfirmDialogOpen(false)}
          open={isCancelConfirmDialogOpen}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: "center" }}>
            承認依頼の取り下げ
          </DialogTitle>
          <DialogContent sx={{ padding: "1rem" }}>
            承認依頼を取り下げます。
            <br />
            ※全ての承認が取り消されます。
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsCancelConfirmDialogOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleCancelApprovalRequest}>取り下げ</Button>
          </DialogActions>
        </Dialog>
        {/* 差戻しダイアログ */}
        <Dialog
          onClose={() => setIsOnRemandDialogOpen(false)}
          open={isOnRemandDialogOpen}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: "center" }}>
            承認依頼の差し戻し
          </DialogTitle>
          <DialogContent sx={{ padding: "1rem" }}>
            <TextField
              label="差戻しコメント"
              multiline
              onChange={(e) => setRemandComment(e.target.value)}
              value={remandComment}
              fullWidth
              minRows={4}
              maxRows={8}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRemand}>差し戻し</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default ResponeseComponent;
